'use client';

import type { ReactNode } from 'react';
import type { MediaCard } from 'src/sections/search-all/utils/search-all-media-cards';

import { useRef, useMemo, useEffect, useCallback } from 'react';

import { HubMediaDetail } from 'src/components/cmui/hub/hub-media-detail';

import { useListMediaDetail } from 'src/sections/common/hooks/handler/use-list-media-detail';
import { indexOfPost, toListCards } from 'src/sections/search-all/utils/search-all-media-order';
import { toPostListDetailOptions } from 'src/sections/search-all/utils/search-all-detail-options';

/**
 * 글 하나를 **소재 상세**로 연다 — 글 상세(`PostDetail`)와 같은 props 를 받아 그 자리를 그대로 갈아 끼운다.
 *
 * 🔑 `posts` 를 주면 그 목록 순서로 이전/다음을 넘긴다 — **화면에 편 순서 그대로** 넘겨야 한다.
 *   안 주면 그 글 하나만 연다(넘김 없음).
 * 🔑 검색어 형광 · 「캠페인 추가」 · 수치 시점 · 북마크 감춤은 글 상세와 같은 이름의 props 로 받는다(`toPostListDetailOptions`).
 * ⚠️ 상세 안에서 넘긴 글은 부르는 쪽에 알리지 않는다 — 부르는 쪽은 «연다·닫는다»만 쥔다.
 * ⚠️ 소셜 글만 다루는 자리다(`socialOnly`) — 「비슷한 소재」에서 광고를 안 묻는다.
 */
export default function PostMediaDetail({
  post,
  posts,
  open,
  onClose,
  searchKeyword,
  clientSearch,
  topicKeywords,
  isBookmark = false,
  enableCampaignAdd = false,
  detailUsesMetricDetailSort = false,
  extraSection,
}: {
  post?: Record<string, unknown> | null;
  /** 화면에 편 순서의 글 목록 — 이전/다음이 따른다 */
  posts?: readonly Record<string, unknown>[] | null;
  open?: boolean;
  onClose?: () => void;
  searchKeyword?: string | readonly string[];
  clientSearch?: string | readonly string[];
  topicKeywords?: readonly string[];
  /** 폴더 다중 선택 중 — 상세 발의 북마크를 감춘다 */
  isBookmark?: boolean;
  enableCampaignAdd?: boolean;
  /** 켜면 정렬 스냅샷, 끄면 가장 최근 수집분으로 수치를 읽는다 */
  detailUsesMetricDetailSort?: boolean;
  /** 화면이 제 글에만 더 싣는 칸(`HubMediaDetail` 에 그대로) — 안 주면 기본 모양 */
  extraSection?: (card: MediaCard) => ReactNode;
}) {
  const list = useMemo(() => (posts && posts.length > 0 ? posts : post ? [post] : []), [posts, post]);
  const cards = useMemo(() => toListCards(list), [list]);
  const { open: openCard, detail } = useListMediaDetail(cards);
  const options = useMemo(
    () => toPostListDetailOptions({ searchKeyword, clientSearch, topicKeywords, enableCampaignAdd, detailUsesMetricDetailSort, isBookmark }),
    [searchKeyword, clientSearch, topicKeywords, enableCampaignAdd, detailUsesMetricDetailSort, isBookmark],
  );

  // 🔑 «연다» 는 부르는 쪽의 `open`·`post` 가 바뀔 때만 — 목록이 바뀔 때마다 다시 열면 넘긴 자리가 처음 글로 튄다
  const latest = useRef({ list, cards, openCard, close: detail.onClose });
  latest.current = { list, cards, openCard, close: detail.onClose };
  useEffect(() => {
    const { list: l, cards: c, openCard: show, close } = latest.current;
    if (!open || !post) {
      close();
      return;
    }
    const at = indexOfPost(l, post);
    show(at >= 0 ? c[at] : toListCards([post])[0]);
  }, [open, post]);

  const handleClose = useCallback(() => {
    detail.onClose();
    onClose?.();
  }, [detail, onClose]);

  return <HubMediaDetail {...detail} onClose={handleClose} socialOnly options={options} extraSection={extraSection} />;
}
